import type { StoredIdentity } from './types'

/**
 * Offline attribution (doc §9): persist the authenticated IDENTITY — never
 * the secret — so a dead-zone boot can attribute work to the right user.
 * The refresh token stays in its httpOnly cookie; sync-time re-auth
 * (`POST /auth/refresh`) is what actually authorizes the replay.
 */

const KEY = 'floorfile.offline.identity'

export function storeIdentity(identity: Omit<StoredIdentity, 'storedAt'>): void {
  if (typeof localStorage === 'undefined') return
  const value: StoredIdentity = { ...identity, storedAt: new Date().toISOString() }
  localStorage.setItem(KEY, JSON.stringify(value))
}

const isString = (value: unknown): value is string => typeof value === 'string'

/**
 * Audit RUN-20. This is the only place the client parses UNAUTHENTICATED
 * input: localStorage is writable by anything running on the origin, and the
 * previous check was `parsed.userId` truthiness before an `as StoredIdentity`
 * cast — so `permissions`, `role` and `companyId` were seeded into the client
 * from an unvalidated blob. Server-side authorization is unaffected (every
 * request is re-authorized against the credential), but a malformed or hostile
 * value could crash the boot or paint a UI the user has no right to.
 *
 * Shape-checked by hand rather than with Zod: `packages/offline` is the D24
 * client runtime and deliberately carries no schema dependency. Anything that
 * does not match is discarded, which lands the user on the login screen — the
 * correct outcome for an identity we cannot trust.
 */
function validateIdentity(value: unknown): StoredIdentity | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const candidate = value as Record<string, unknown>
  if (!isString(candidate['userId']) || candidate['userId'].length === 0) return undefined
  for (const key of ['companyId', 'email', 'firstName', 'lastName', 'role', 'storedAt']) {
    if (!isString(candidate[key])) return undefined
  }
  const permissions = candidate['permissions']
  if (!Array.isArray(permissions) || !permissions.every(isString)) return undefined
  const totpEnabled = candidate['totpEnabled']
  if (totpEnabled !== undefined && typeof totpEnabled !== 'boolean') return undefined
  return value as StoredIdentity
}

export function readIdentity(): StoredIdentity | undefined {
  if (typeof localStorage === 'undefined') return undefined
  const raw = localStorage.getItem(KEY)
  if (!raw) return undefined
  try {
    return validateIdentity(JSON.parse(raw))
  } catch {
    return undefined
  }
}

export function clearIdentity(): void {
  if (typeof localStorage === 'undefined') return
  localStorage.removeItem(KEY)
}
